import { useEffect, useRef, useState, useCallback } from "react";
import { Input } from "./ui/input";

// Lazily-loaded, module-cached offline datasets (no third-party API).
let _airlines = null;
let _airports = null;
let _airportByCode = null;

async function loadAirlines() {
  if (!_airlines) {
    const mod = await import("../data/airlines.json");
    _airlines = mod.default || mod;
  }
  return _airlines;
}

async function loadAirports() {
  if (!_airports) {
    const mod = await import("../data/airports.json");
    _airports = mod.default || mod;
    _airportByCode = new Map(_airports.map((a) => [a[0], a]));
  }
  return _airports;
}

const norm = (s) => (s || "").toString().trim().toLowerCase();

// Well-known major hubs float to the top when several airports match the same city.
const MAJOR_HUBS = new Set([
  "LHR","LGW","STN","LTN","LCY","JFK","EWR","LGA","LAX","ORD","ATL","DFW","SFO","MIA","SEA","BOS","IAD","DEN","PHX","IAH","MCO",
  "CDG","ORY","AMS","FRA","MUC","MAD","BCN","FCO","MXP","LIS","ZRH","GVA","VIE","BRU","CPH","ARN","OSL","HEL","DUB","MAN","EDI","IST","SAW","ATH","PRG","WAW","BUD","OTP",
  "DXB","AUH","DOH","JED","RUH","KWI","BAH","CAI","TLV","AMM",
  "SIN","HKG","NRT","HND","ICN","GMP","PEK","PKX","PVG","CAN","CTU","SZX","BKK","KUL","CGK","MNL","DEL","BOM","BLR","MAA","HYD","CCU","TPE","SGN","HAN",
  "SYD","MEL","BNE","PER","AKL",
  "YYZ","YVR","YUL","GRU","GIG","EZE","SCL","BOG","LIM","MEX","CUN","PTY",
  "JNB","CPT","NBO","ADD","LOS","CMN",
]);

function rankAirlines(list, q) {
  const query = norm(q);
  if (!query) return [];
  const up = query.toUpperCase();
  const out = [];
  for (const [code, name] of list) {
    const nl = name.toLowerCase();
    let score = -1;
    if (code === up) score = 0;
    else if (code.startsWith(up)) score = 1;
    else if (nl.startsWith(query)) score = 2;
    else if (nl.includes(query)) score = 3;
    if (score >= 0) out.push([score, code, name]);
    if (out.length > 400) break;
  }
  out.sort((a, b) => a[0] - b[0] || a[2].localeCompare(b[2]));
  return out.slice(0, 30).map(([, code, name]) => ({ code, name }));
}

function rankAirports(list, q) {
  const query = norm(q);
  if (!query) return [];
  const up = query.toUpperCase();
  const out = [];
  for (const [code, name, city, country] of list) {
    const cl = (city || "").toLowerCase();
    const nl = (name || "").toLowerCase();
    let score = -1;
    if (code === up) score = 0;
    else if (code.startsWith(up)) score = 1;
    else if (cl.startsWith(query)) score = 2;
    else if (nl.startsWith(query)) score = 3;
    else if (cl.includes(query) || nl.includes(query)) score = 4;
    if (score >= 0) out.push([score, code, name, city, country]);
    if (out.length > 800) break;
  }
  out.sort((a, b) =>
    a[0] - b[0]
    || ((MAJOR_HUBS.has(b[1]) ? 1 : 0) - (MAJOR_HUBS.has(a[1]) ? 1 : 0))
    || (a[3] || "").localeCompare(b[3] || "")
  );
  return out.slice(0, 30).map(([, code, name, city, country]) => ({ code, name, city, country }));
}

function Dropdown({ items, onPick, renderItem, testid }) {
  if (!items.length) return null;
  return (
    <ul
      data-testid={testid}
      className="absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-md border border-slate-200 bg-white py-1 shadow-lg"
    >
      {items.map((it, i) => (
        <li key={`${it.code}-${i}`}>
          <button
            type="button"
            data-testid={`${testid}-option-${it.code}`}
            onMouseDown={(e) => {
              e.preventDefault();
              onPick(it);
            }}
            className="flex w-full items-start gap-2 px-3 py-1.5 text-left text-sm hover:bg-blue-50"
          >
            {renderItem(it)}
          </button>
        </li>
      ))}
    </ul>
  );
}

export function AirlineAutocomplete({ value, onChange, onSelect, testid = "airline-search", ...rest }) {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const dataRef = useRef(null);
  const wrapRef = useRef(null);

  useEffect(() => {
    loadAirlines().then((d) => { dataRef.current = d; });
  }, []);

  const search = useCallback((q) => {
    const d = dataRef.current;
    if (!d) return;
    setItems(rankAirlines(d, q));
  }, []);

  const handleType = (v) => {
    onChange(v);
    search(v);
    setOpen(true);
  };

  return (
    <div ref={wrapRef} className="relative">
      <Input
        {...rest}
        value={value || ""}
        onChange={(e) => handleType(e.target.value)}
        onFocus={() => { if (value) { search(value); setOpen(true); } }}
        onBlur={() => setTimeout(() => setOpen(false), 120)}
        autoComplete="off"
        data-testid={testid}
      />
      {open && (
        <Dropdown
          items={items}
          testid={`${testid}-list`}
          onPick={(it) => {
            onSelect(it);
            setOpen(false);
          }}
          renderItem={(it) => (
            <>
              <span className="font-pnr font-semibold text-slate-900 w-8 shrink-0">{it.code}</span>
              <span className="text-slate-600">{it.name}</span>
            </>
          )}
        />
      )}
    </div>
  );
}

export function AirportAutocomplete({ value, onChange, onSelect, onCodeResolved, testid = "airport-search", ...rest }) {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const dataRef = useRef(null);

  useEffect(() => {
    loadAirports().then((d) => { dataRef.current = d; });
  }, []);

  const search = useCallback((q) => {
    const d = dataRef.current;
    if (!d) return;
    setItems(rankAirports(d, q));
  }, []);

  const handleType = (v) => {
    const up = v.toUpperCase();
    onChange(up);
    search(up);
    setOpen(true);
    // Typing a full valid IATA code auto-populates the city.
    if (up.length === 3 && _airportByCode && _airportByCode.has(up)) {
      const a = _airportByCode.get(up);
      onCodeResolved?.({ code: a[0], name: a[1], city: a[2], country: a[3] });
    }
  };

  return (
    <div className="relative">
      <Input
        {...rest}
        value={value || ""}
        onChange={(e) => handleType(e.target.value)}
        onFocus={() => { if (value) { search(value); setOpen(true); } }}
        onBlur={() => setTimeout(() => setOpen(false), 120)}
        autoComplete="off"
        data-testid={testid}
      />
      {open && (
        <Dropdown
          items={items}
          testid={`${testid}-list`}
          onPick={(it) => {
            onSelect(it);
            setOpen(false);
          }}
          renderItem={(it) => (
            <>
              <span className="font-pnr font-semibold text-slate-900 w-9 shrink-0">{it.code}</span>
              <span className="text-slate-600">
                <span className="text-slate-900">{it.city}</span>
                <span className="text-slate-400"> · {it.name}{it.country ? `, ${it.country}` : ""}</span>
              </span>
            </>
          )}
        />
      )}
    </div>
  );
}
